import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import { FileButton, Field, Input, TextArea } from "@/components/DestinationForm";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SOCIALS = ["instagram", "linkedin", "facebook", "twitter", "youtube", "tiktok"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function MyProfile() {
  const { checkAuth } = useAuth();
  const [p, setP] = useState(null);
  const [name, setName] = useState("");
  const [schema, setSchema] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [em, setEm] = useState({ new_email: "", password: "" });

  useEffect(() => {
    api.get("/account/profile").then(({ data }) => { setP(data.profile || {}); setName(data.name || ""); setSchema(data.field_schema || []); }).catch(() => {});
    const q = new URLSearchParams(window.location.search);
    if (q.get("email_changed") === "1") toast.success("Email updated. Please sign in again if prompted.");
    if (q.get("email_changed") === "0") toast.error("Email change link was invalid or expired.");
  }, []);

  if (!p) return <AppLayout><div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div></AppLayout>;

  const set = (k, v) => setP({ ...p, [k]: v });
  const setSocial = (k, v) => setP({ ...p, socials: { ...(p.socials || {}), [k]: v } });
  const customFields = p.custom_fields || [];
  const customSocials = p.custom_socials || [];
  const setCF = (list) => setP({ ...p, custom_fields: list });
  const setCS = (list) => setP({ ...p, custom_socials: list });
  const move = (list, i, dir) => { const a = [...list]; const j = i + dir; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/account/profile", { profile: p, name }); setP(data.profile); toast.success("Profile saved"); checkAuth?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changeEmail = async () => {
    if (!em.new_email || !em.password) return toast.error("Enter your new email and current password");
    try { const { data } = await api.post("/account/change-email", em); toast.success(`Confirmation sent to ${data.sent_to}. Click the link to finish.`); setEm({ new_email: "", password: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <AppLayout>
      <h1 className="text-2xl font-display font-extrabold mb-1">My profile</h1>
      <p className="text-sm text-muted-foreground mb-6">Your personal and business details. Reused as defaults when you create QR codes and landing pages.</p>
      <div className="max-w-2xl space-y-6">

        {/* Photo & logo */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <h3 className="font-display font-bold">Photo & branding</h3>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              {p.photo ? <img src={p.photo} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center text-primary"><Icon name="User" className="h-6 w-6" /></div>}
              <FileButton accept="image/*" label="Profile photo" testid="profile-photo" onUrl={(url) => set("photo", url)} />
            </div>
            <div className="flex items-center gap-3">
              {p.logo ? <img src={p.logo} alt="" className="h-16 w-16 rounded-xl object-contain bg-white p-1" /> : <div className="h-16 w-16 rounded-xl bg-primary/10 grid place-items-center text-primary"><Icon name="Building2" className="h-6 w-6" /></div>}
              <FileButton accept="image/*" label="Company logo" testid="profile-logo" onUrl={(url) => set("logo", url)} />
            </div>
          </div>
        </div>

        {/* Standard fields */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <h3 className="font-display font-bold">Personal & business details</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Display name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-display-name" /></Field>
            <Field label="Job title"><Input value={p.job_title || ""} onChange={(e) => set("job_title", e.target.value)} data-testid="profile-job" /></Field>
            <Field label="First name"><Input value={p.first_name || ""} onChange={(e) => set("first_name", e.target.value)} /></Field>
            <Field label="Last name"><Input value={p.last_name || ""} onChange={(e) => set("last_name", e.target.value)} /></Field>
            <Field label="Company"><Input value={p.company || ""} onChange={(e) => set("company", e.target.value)} /></Field>
            <Field label="Website"><Input value={p.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="profile-website" /></Field>
            <Field label="Mobile"><Input value={p.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field label="Work phone"><Input value={p.work_phone || ""} onChange={(e) => set("work_phone", e.target.value)} /></Field>
            <Field label="WhatsApp"><Input value={p.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="Public email"><Input value={p.public_email || ""} onChange={(e) => set("public_email", e.target.value)} /></Field>
            <Field label="City"><Input value={p.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Country"><Input value={p.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="profile-country" /></Field>
            <Field label="Address"><Input value={p.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="Google Maps URL"><Input value={p.maps_url || ""} onChange={(e) => set("maps_url", e.target.value)} /></Field>
          </div>
          <Field label="Bio / about"><TextArea value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={3} /></Field>
        </div>

        {/* Socials */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <h3 className="font-display font-bold">Social links</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {SOCIALS.map((s) => <Field key={s} label={s}><Input value={p.socials?.[s] || ""} onChange={(e) => setSocial(s, e.target.value)} /></Field>)}
          </div>
          <div className="pt-2 border-t border-border">
            <div className="text-sm font-semibold mb-2">Custom social links</div>
            {customSocials.map((cs, i) => (
              <div key={cs.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center" data-testid={`custom-social-${i}`}>
                <Input value={cs.label} placeholder="Label (e.g. Behance)" onChange={(e) => setCS(customSocials.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                <Input value={cs.url} placeholder="https://..." onChange={(e) => setCS(customSocials.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} />
                <button onClick={() => setCS(customSocials.filter((_, j) => j !== i))} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-accent"><Icon name="X" className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <button data-testid="add-custom-social" onClick={() => setCS([...customSocials, { id: uid(), icon: "Link", label: "", url: "" }])} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">+ Add custom link</button>
          </div>
        </div>

        {/* Custom fields (reorderable) */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
          <h3 className="font-display font-bold">Custom fields</h3>
          {customFields.map((f, i) => (
            <div key={f.id} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center" data-testid={`custom-field-${i}`}>
              <div className="flex flex-col">
                <button onClick={() => setCF(move(customFields, i, -1))} className="text-muted-foreground hover:text-foreground"><Icon name="ChevronUp" className="h-3.5 w-3.5" /></button>
                <button onClick={() => setCF(move(customFields, i, 1))} className="text-muted-foreground hover:text-foreground"><Icon name="ChevronDown" className="h-3.5 w-3.5" /></button>
              </div>
              <Input value={f.label} placeholder="Label" onChange={(e) => setCF(customFields.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
              <Input value={f.value} placeholder="Value or link" onChange={(e) => setCF(customFields.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
              <button onClick={() => setCF(customFields.filter((_, j) => j !== i))} className="h-9 w-9 grid place-items-center rounded-lg border border-border hover:bg-accent"><Icon name="X" className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button data-testid="add-custom-field" onClick={() => setCF([...customFields, { id: uid(), label: "", value: "", type: "text" }])} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">+ Add field</button>
        </div>

        {/* Admin-defined fields */}
        {schema.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="admin-defined-fields">
            <h3 className="font-display font-bold">Additional details</h3>
            {schema.filter((f) => f.visible !== false).map((f) => {
              const key = `field_${f.id}`;
              return (
                <Field key={f.id} label={`${f.label}${f.required ? " *" : ""}`}>
                  {f.type === "longtext" ? <TextArea value={p[key] || ""} onChange={(e) => set(key, e.target.value)} rows={3} />
                    : f.type === "checkbox" ? <input type="checkbox" checked={!!p[key]} onChange={(e) => set(key, e.target.checked)} />
                    : f.type === "dropdown" ? <select value={p[key] || ""} onChange={(e) => set(key, e.target.value)} className="inp"><option value="">Select…</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    : <Input type={f.type === "date" ? "date" : "text"} value={p[key] || ""} onChange={(e) => set(key, e.target.value)} />}
                </Field>
              );
            })}
          </div>
        )}

        <button data-testid="save-profile" onClick={save} disabled={saving} className="rounded-xl bg-primary text-primary-foreground px-6 py-2.5 text-sm font-bold hover:opacity-90 disabled:opacity-60">{saving ? "Saving…" : "Save profile"}</button>

        {/* Security: change password */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="change-password-card">
          <h3 className="font-display font-bold flex items-center gap-2"><Icon name="KeyRound" className="h-4 w-4" />Change password</h3>
          <p className="text-sm text-muted-foreground -mt-1">Signed in with Google? Leave “Current password” blank to set one for the first time.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Current password"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="cp-current" /></Field>
            <Field label="New password"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="cp-new" /></Field>
            <Field label="Confirm new"><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="cp-confirm" /></Field>
          </div>
          <button data-testid="cp-submit" onClick={changePassword} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent">Update password</button>
        </div>

        {/* Security: change email */}
        <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="change-email-card">
          <h3 className="font-display font-bold flex items-center gap-2"><Icon name="Mail" className="h-4 w-4" />Change email</h3>
          <p className="text-sm text-muted-foreground">You'll get a confirmation link at the new address. The change applies only after you confirm.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="New email"><Input type="email" value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} data-testid="ce-email" /></Field>
            <Field label="Current password"><Input type="password" value={em.password} onChange={(e) => setEm({ ...em, password: e.target.value })} data-testid="ce-password" /></Field>
          </div>
          <button data-testid="ce-submit" onClick={changeEmail} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent">Send confirmation</button>
        </div>
      </div>
    </AppLayout>
  );
}
